import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';

import {
    BillingInvoicesGate,
    pageNumberChanged,
    billingState$,
    filtersChanged,
    filtersReset,
    pageNumber$,
    sortingsChanged,
    tableState$,
} from 'models/profile/billingIvoice';
import {BillingInvoiceRawRow} from 'types/billing';

interface Props {
    configFunc: Array<any>;
    dataMapper: (
        data: BillingInvoiceRawRow[] | undefined,
        t: TFunction,
    ) => Partial<BillingInvoiceRawRow>[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const BillingInvoicesGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
}: Props) => {
    const {t} = useTranslation();
    const state = useStore(billingState$);
    const pageNumber = useStore(pageNumber$);
    const {filters, sortings} = useStore(tableState$);
    useGate(BillingInvoicesGate);
    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc,
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, sortings, filters],
    );
    const mappedData = React.useMemo(() => dataMapper(state?.data?.content ?? [], t), [
        dataMapper,
        state,
        t,
    ]);

    return (
        <Table
            columns={columns}
            data={mappedData}
            totalRowsCount={state.data?.totalElements}
            pageCount={state.data?.totalPages}
            pageIndex={pageNumber}
            onPageChange={pageNumberChanged}
            isLoading={state.loading}
            headerControls={headerControls}
            userConfig={{
                gridName: 'billingInvoicesGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            exportConfig={exportConfig}
        />
    );
};
